/**
 * 公開フォーム（未認証アクセス可、ProtectedRoutes 外）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-12.3（Phase 24b）
 *
 * 動作:
 *   1. getPublicForm Callable でフォーム定義取得（status / 公開期間チェック含む）
 *   2. FormElementRenderer でフォームをレンダリング
 *   3. submit ボタン → submitForm Callable
 *   4. 成功: ThanksScreen に切替（redirectUrl があれば自動遷移）
 *   5. エラー: トースト + ステイ
 */
import type {
  FormElement,
  FormTheme,
  PublicFormDynamicOptions,
} from '@goenhub/shared';
import { validateCheckboxSelection } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import { AlertCircle, Loader2, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';


import { ThanksScreen } from './ThanksScreen';

import { usePublicLandingGoogleTag } from '@/hooks/useLandings';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';
import { usePublicGoogleTagDefault } from '@/hooks/useSettings';
import { functions } from '@/lib/firebase';
import { resolveEffectiveTag, useGoogleTag } from '@/lib/googleTag';
import { getOrCreateVisitorId } from '@/lib/visitor';
import { FormElementRenderer } from '@/pages/forms/FormElementRenderer';


interface PublicFormResponse {
  ok: boolean;
  form?: {
    id: string;
    title: string;
    description?: string;
    elements: FormElement[];
    thanksMessage?: string;
    redirectUrl?: string;
    theme?: FormTheme | null;
    dynamicOptions?: PublicFormDynamicOptions;
    /** 定員残数（capacity 未設定なら undefined） */
    remainingCapacity?: number;
    /** 残枠数を公開表示するかのフォーム設定 */
    showRemainingCount?: boolean;
  };
  reason?: 'not_found' | 'closed' | 'not_yet_open' | 'expired' | 'capacity_reached';
  /** capacity_reached の時のカスタムメッセージ */
  capacityFullMessage?: string;
}

interface SubmitResponse {
  ok: boolean;
  submissionId: string;
  thanksMessage: string;
  redirectUrl: string | null;
}

const getPublicFormFn = httpsCallable<
  {
    formId: string;
    /** v3.2.18: SA リフェラル UID（?sa= から）。サーバ側で個別説明枠を絞り込む。 */
    sa?: string | null;
  },
  PublicFormResponse
>(functions, 'getPublicForm');

const submitFormFn = httpsCallable<
  {
    formId: string;
    campaignId?: string | null;
    values: Record<string, string | string[]>;
    lineUid?: string;
    /** P1: 流入元タグ（URL ?src= から） */
    src?: string | null;
    /** P1: メール由来 contactId（URL ?cid= から） */
    cid?: string | null;
    /** referral タグ（?ref= から、SNS 広告 / QR 等） */
    ref?: string | null;
    /** v3.2.18: SA リフェラル UID（?sa= から）。Deal.salesAgentUid に自動セット。 */
    sa?: string | null;
    /** 永続 visitor ID（localStorage 由来）。retroactive backfill のキー */
    visitorId?: string | null;
  },
  SubmitResponse
>(functions, 'submitForm');

const ERROR_MESSAGE: Record<string, { title: string; body: string }> = {
  not_found: {
    title: 'フォームが見つかりません',
    body: 'URL が正しいかご確認ください。',
  },
  closed: {
    title: 'フォームの受付は終了しました',
    body: 'お問い合わせは管理者にご連絡ください。',
  },
  not_yet_open: {
    title: 'フォームはまだ受付を開始していません',
    body: '公開開始時刻になりましたら受付可能です。',
  },
  expired: {
    title: 'フォームの受付期間が終了しました',
    body: 'ご了承ください。',
  },
  capacity_reached: {
    title: '定員に達しました',
    body: 'お申し込みありがとうございます。本フォームは規定の定員に達したため、受付を終了しました。',
  },
};

interface PublicFormPageProps {
  /** Phase 24f: LIFF 経由ラッパーから渡される LINE userId */
  lineUid?: string;
  /** ルートパラメータの代わりに直接 formId を渡したい場合（LIFF ラッパー用） */
  formIdOverride?: string;
  /** 同上、campaignId を直接渡す */
  campaignIdOverride?: string | null;
}

export function PublicFormPage({
  lineUid,
  formIdOverride,
  campaignIdOverride,
}: PublicFormPageProps = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const id = formIdOverride ?? routeId;
  const campaignId = campaignIdOverride ?? params.get('campaignId');
  // P1: 流入元タグ（mail / line / landing / referral / direct）
  const src = params.get('src');
  // P1: メール由来 contactId（CTA に ?cid= が付いていた場合）
  const cidParam = params.get('cid');
  // referral タグ（SNS 広告 / QR / その他外部チャネル識別）
  const refParam = params.get('ref');
  // v3.2.18: SA リフェラル UID（SaFormsPage から共有された営業リンク）
  const saParam = params.get('sa');
  // LP 経由（?lp=<landingId>）の場合は当該 LP の個別 Google タグを継承する
  const lpParam = params.get('lp');
  // 配信停止フッター等の ?email= で受信者メールを自動補完する
  const emailParam = params.get('email');

  // body の overflow/height 制約を解除（公開ページ共通）
  usePublicPageMode();

  // Google タグ: LP 個別（?lp= 由来）があれば優先、なければグローバル既定。
  const { data: landingGoogleTag } = usePublicLandingGoogleTag(lpParam);
  const { data: googleTagDefault } = usePublicGoogleTagDefault();
  useGoogleTag(resolveEffectiveTag(landingGoogleTag, googleTagDefault));

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PublicFormResponse['form'] | null>(null);
  const [errorReason, setErrorReason] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{
    thanksMessage: string;
    redirectUrl: string | null;
  } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [capacityFullMessage, setCapacityFullMessage] = useState<string | null>(null);
  // 2026-08-31: 送信を試みて弾かれた後だけ、各フィールドの注意表示を出す
  const [showValidation, setShowValidation] = useState(false);

  // フォーム取得（saParam があればサーバ側で個別説明枠が絞り込まれる）
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setErrorDetail(null);
    setCapacityFullMessage(null);
    getPublicFormFn({ formId: id, sa: saParam || null })
      .then((res) => {
        if (!res.data.ok) {
          setErrorReason(res.data.reason ?? 'not_found');
          if (res.data.reason === 'capacity_reached' && res.data.capacityFullMessage) {
            setCapacityFullMessage(res.data.capacityFullMessage);
          }
        } else {
          setData(res.data.form ?? null);
        }
      })
      .catch((err) => {
        // Callable 例外時 (CORS / IAM / network 失敗) は本当の原因を残しておく。
        // Firestore に doc が無い not_found は ok:false で返るので、ここに来る
        // のはほぼインフラ・権限系のエラー。
        setErrorReason('not_found');
        const code =
          err && typeof err === 'object' && 'code' in err
            ? String((err as { code: string }).code)
            : '';
        const msg = err instanceof Error ? err.message : String(err);
        setErrorDetail(code ? `${code}: ${msg}` : msg);
        console.error('[PublicFormPage] getPublicForm failed', err);
      })
      .finally(() => setLoading(false));
  }, [id, saParam]);

  // ?email= が指定されていればメールフィールドを自動補完（配信停止フォーム用）。
  // ユーザーが既に入力していれば上書きしない。
  useEffect(() => {
    if (!data || !emailParam) return;
    const emailField = data.elements.find(
      (el) =>
        el.kind === 'field' &&
        (el as { fieldType?: string }).fieldType === 'email',
    );
    if (!emailField) return;
    setValues((s) =>
      s[emailField.id] ? s : { ...s, [emailField.id]: emailParam },
    );
  }, [data, emailParam]);

  const brandColor = data?.theme?.brandColor;
  const bgColor = data?.theme?.backgroundColor || undefined;

  const handleChange = (fieldId: string, v: string | string[]) => {
    setValues((s) => ({ ...s, [fieldId]: v }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data || !id) return;

    // checkbox（複数選択）の選択数チェック (2026-08-31)。
    // チェックボックス群はブラウザ標準の required で表現できないため、送信前にここで弾く。
    // サーバ (submitForm) も同じ判定を持つので、これは体験のための先出し。
    const selectionErrors = data.elements.flatMap((el) =>
      el.kind === 'field'
        ? [validateCheckboxSelection(el, values[el.id])].filter(
            (m): m is string => m !== null,
          )
        : [],
    );
    if (selectionErrors.length > 0) {
      setShowValidation(true);
      setSubmitError(selectionErrors.join('\n'));
      return;
    }

    setShowValidation(false);
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await submitFormFn({
        formId: id,
        campaignId: campaignId ?? null,
        values,
        ...(lineUid ? { lineUid } : {}),
        src: src || null,
        cid: cidParam || null,
        ref: refParam || null,
        sa: saParam || null,
        visitorId: getOrCreateVisitorId(),
      });
      setSubmitted({
        thanksMessage: res.data.thanksMessage,
        redirectUrl: res.data.redirectUrl,
      });
      // SPA では送信完了が別 URL にならないため、GTM が確実にコンバージョンを
      // 検知できるよう dataLayer イベントを push する（計測設定は代理店側で行う）。
      const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event: 'form_submit_complete',
        formId: id,
        campaignId: campaignId ?? null,
        submissionId: res.data.submissionId,
      });
      // リダイレクト URL があれば自動遷移
      if (res.data.redirectUrl) {
        setTimeout(() => {
          window.location.href = res.data.redirectUrl!;
        }, 1500);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '送信に失敗しました';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (errorReason) {
    const baseMsg = ERROR_MESSAGE[errorReason] ?? ERROR_MESSAGE.not_found;
    // capacity_reached でフォームにカスタムメッセージが設定されていればそれを優先
    const msg =
      errorReason === 'capacity_reached' && capacityFullMessage
        ? { title: capacityFullMessage, body: baseMsg.body }
        : baseMsg;
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '(unknown)';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-lg bg-white border border-gray-200 p-6 text-center space-y-3 shadow-sm">
          <AlertCircle className="size-10 mx-auto text-amber-500" />
          <h1 className="text-base font-bold text-gray-900">{msg.title}</h1>
          <p className="text-xs text-gray-600 leading-relaxed">{msg.body}</p>
          {errorDetail && (
            <details className="text-left mt-3 rounded border border-gray-200 bg-gray-50 p-2">
              <summary className="text-[11px] text-gray-500 cursor-pointer">
                技術的な詳細（管理者向け）
              </summary>
              <div className="mt-2 text-[10px] font-mono text-gray-700 break-all">
                {errorDetail}
              </div>
              <div className="mt-1 text-[10px] text-gray-500">
                project: <span className="font-mono">{projectId}</span>
                {' / '}form ID: <span className="font-mono">{id}</span>
              </div>
            </details>
          )}
          {!errorDetail && errorReason === 'not_found' && (
            <p className="text-[10px] text-gray-400 mt-2">
              プロジェクト: <span className="font-mono">{projectId}</span>
            </p>
          )}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <ThanksScreen
        message={submitted.thanksMessage}
        brandColor={brandColor}
        backgroundColor={bgColor}
        logoUrl={data?.theme?.logoUrl}
        redirectingTo={submitted.redirectUrl}
      />
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4" style={{ colorScheme: 'light' }}>
      {/* 横幅: スマホ=現状(max-w-md/448px) / タブレット(md≥768)=640px / PC(lg≥1024)=850px */}
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md md:max-w-[640px] lg:max-w-[850px] mx-auto space-y-4"
      >
        {data.theme?.logoUrl && (
          <img src={data.theme.logoUrl} alt="" className="max-h-20 mx-auto" />
        )}
        <div
          className="rounded-lg bg-white border border-gray-200 p-6 space-y-4 shadow-sm"
          style={bgColor ? { backgroundColor: bgColor } : undefined}
        >
          <h1
            className="text-xl font-bold text-center text-gray-900 whitespace-pre-line"
            style={brandColor ? { color: brandColor } : undefined}
          >
            {data.title}
          </h1>
          {data.description && (
            <p className="text-[13px] text-gray-600 text-center whitespace-pre-line">
              {data.description}
            </p>
          )}

          {data.showRemainingCount && typeof data.remainingCapacity === 'number' && (
            <div className="flex justify-center">
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[12px] font-semibold ${
                  data.remainingCapacity <= 3
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}
              >
                残り {data.remainingCapacity} 枠
              </span>
            </div>
          )}

          <div className="space-y-4 pt-2">
            {data.elements.map((el) => (
              <FormElementRenderer
                key={el.id}
                element={el}
                values={values}
                onChange={handleChange}
                brandColor={brandColor}
                dynamicOptions={data.dynamicOptions}
                showValidation={showValidation}
              />
            ))}
          </div>

          {submitError && (
            <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-[13px] text-red-700 whitespace-pre-line">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-2 py-2.5 rounded-md text-white font-semibold text-[14px] hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: brandColor ?? '#4a7cff' }}
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                送信中…
              </>
            ) : (
              <>
                <Send className="size-4" />
                送信する
              </>
            )}
          </button>
        </div>
        <p className="text-[10px] text-gray-400 text-center">
          GoEnHub Forms
        </p>
      </form>
    </div>
  );
}
